import React, { createContext, useContext, useState, ReactNode, Key, useCallback } from "react";
import {
  GetAccountingStatementsListParams,
  PaginatedApiParams
} from "../types/reconciliation";
import { isEmpty } from "lodash";

interface AccountingStatementsTableContextType {
  page: number;
  pageSize: number;
  ordering: string | undefined;
  roreJobStatus: Key[];
  reconciliationJobStatus: Key[];
  dateCreation: Key[];
  documentName: Key[];
  ecNumber: Key[];
  ecPeriod: Key[];
  broker: Key[];
  credit: Key[];
  debit: Key[];
  balance: Key[];
  withHoldingTax: Key[];
  toBeReconciled: Key[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOrdering: (ordering: string) => void;
  setRoreJobStatus: (status: Key[]) => void;
  setReconciliationJobStatus: (status: Key[]) => void;
  setDateCreation: (dateCreation: Key[]) => void;
  setDocumentName: (documentName: Key[]) => void;
  setEcNumber: (ecNumber: Key[]) => void;
  setEcPeriod: (ecPeriod: Key[]) => void;
  setBroker: (broker: Key[]) => void;
  setCredit: (credit: Key[]) => void;
  setDebit: (debit: Key[]) => void;
  setBalance: (balance: Key[]) => void;
  setWithHoldingTax: (withHoldingTax: Key[]) => void;
  setToBeReconciled: (toBeReconciled: Key[]) => void;
  getParamsForApiRequest: (defaultFilters?: GetAccountingStatementsListParams) => (GetAccountingStatementsListParams & PaginatedApiParams);
}

const AccountingStatementTableContext = createContext<AccountingStatementsTableContextType | undefined>(undefined);

export const AccountingStatementsTableProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);
  const [reconciliationJobStatus, setReconciliationJobStatus] = useState<Key[]>([]);
  const [roreJobStatus, setRoreJobStatus] = useState<Key[]>([]);
  const [dateCreation, setDateCreation] = useState<Key[]>([]);
  const [documentName, setDocumentName] = useState<Key[]>([]);
  const [ecNumber, setEcNumber] = useState<Key[]>([]);
  const [ecPeriod, setEcPeriod] = useState<Key[]>([]);
  const [broker, setBroker] = useState<Key[]>([]);
  const [credit, setCredit] = useState<Key[]>([]);
  const [debit, setDebit] = useState<Key[]>([]);
  const [balance, setBalance] = useState<Key[]>([]);
  const [withHoldingTax, setWithHoldingTax] = useState<Key[]>([]);
  const [toBeReconciled, setToBeReconciled] = useState<Key[]>([]);

  const getParamsForApiRequest = useCallback((defaultFilters?: GetAccountingStatementsListParams) => {
    return {
      page,
      page_size: pageSize,
      ...(!isEmpty(ordering) && { ordering: ordering }),
      ...(!isEmpty(defaultFilters) && { ...defaultFilters }),
      ...(!isEmpty(toBeReconciled) && { to_be_reconciled: toBeReconciled?.join(",") }),
      ...(!isEmpty(reconciliationJobStatus) && { reconciliation_job_status: reconciliationJobStatus?.join(",") }),
      ...(!isEmpty(roreJobStatus) && { rore_job_status: roreJobStatus?.join(",") }),
      ...(!isEmpty(dateCreation) && {
        date_creation_after: dateCreation?.at(0) as string,
        date_creation_before: dateCreation?.at(1) as string
      }),
      ...(!isEmpty(broker) && { broker: broker?.join(",") }),
      ...(!isEmpty(ecPeriod) && {
        ec_period_min: ecPeriod?.at(0) as string,
        ec_period_max: ecPeriod?.at(1) as string
      }),
      ...(!isEmpty(ecNumber) && { ec_number: ecNumber?.join(",") }),
      ...(!isEmpty(documentName) && { document_name: documentName?.join(",") }),
    }
  }, [broker, dateCreation, documentName, ecNumber, ecPeriod, ordering, page, pageSize, reconciliationJobStatus, roreJobStatus, toBeReconciled]);


  const value: AccountingStatementsTableContextType = {
    page,
    pageSize,
    ordering,
    roreJobStatus,
    reconciliationJobStatus,
    dateCreation,
    documentName,
    ecNumber,
    ecPeriod,
    broker,
    credit,
    debit,
    balance,
    withHoldingTax,
    toBeReconciled,
    setPage,
    setPageSize,
    setOrdering,
    setRoreJobStatus,
    setReconciliationJobStatus,
    setDateCreation,
    setDocumentName,
    setEcNumber,
    setEcPeriod,
    setBroker,
    setCredit,
    setDebit,
    setBalance,
    setWithHoldingTax,
    setToBeReconciled,
    getParamsForApiRequest
  };

  return (
    <AccountingStatementTableContext.Provider value={value}>
      {children}
    </AccountingStatementTableContext.Provider>
  );
};

export const useAccountingStatementsTable = (): AccountingStatementsTableContextType => {
  const context = useContext(AccountingStatementTableContext);
  if (context === undefined) {
    throw new Error('useReconciliationTable deve essere utilizzato all\'interno di un ReconciliationTableProvider');
  }
  return context;
};